import type { JsonValue, JsonObject } from 'type-fest';

import {
  prepareAndMakeRequest,
  type PartViewerPageData,
  type LegacyFileTaskStatusData,
  type LegacyFileTaskStatus,
  type TechnicalDataPackage,
  type SharedTechnicalDataPackage,
} from '@frontend/api';
import type { loadUtils } from '@frontend/partviewer';

type AnnotationData = {
  initialComment: string;
  partMasterVersion: string;
  cadViewerState?: string;
  commentedOnQuoteItemId?: string;
  featureIds?: string[];
  feedbackItemIds?: string[];
  measurement?: null | {
    caliperObjects: loadUtils.LiteObject[];
    distance: number;
    viewerState: string;
    cadViewerState: string;
  };
  partFile?: string;
  partInterrogationFileId?: string;
  pdfAnnotations?: JsonObject[];
  pdfMeasurements?: JsonObject[];
  resolved?: boolean;
  selection?:
    | null
    | (JsonObject & { viewerState: string; cadViewerState: string });
  task?: Task;
  viewerState?: string;
};

export const CHANNEL_TYPES = {
  ACTIONS: 'actions',
  MSC: 'msc',
  PENN: 'penn',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const;

type ChannelData = {
  partMasterVersion: string;
  type: (typeof CHANNEL_TYPES)[keyof typeof CHANNEL_TYPES];
  accessToAllPartFiles?: boolean;
  accessToPrimaryFile?: boolean;
  name?: string;
  permissionToDownloadFiles?: boolean;
  permissionToUploadFiles?: boolean;
  supportingFilesAccess?: {
    accessGranted?: boolean;
    filename: string;
    partFileUuid: string;
  }[];
};

//TODO: Refine Comment definition
type Comment = { commentId: string };

type Task = { assignedToId: string; dueDate?: string };

//Inferred by annotationSchema in entitiesStore.receiveAnnotation and also noting id is used by response from channels.create
type Annotation = {
  comments: Comment[];
  task: Task;
};

//Inferred by channelSchema in entitiesStore.receiveChannel and also noting id is used by response from channels.create
type Channel = {
  id: string;
  annotations: Annotation[];
  shares: Share[];
  channelEvents: Record<string, { channelId: string }>;
  accessToAllFiles: boolean;
  accessToPrimaryFile: boolean;
  mscSpecialist: unknown;
  name: string | null;
  permissionToDownloadFiles: boolean;
  permissionToUploadFiles: boolean;
  supportingFilesAccess: unknown[];
  type: (typeof CHANNEL_TYPES)[keyof typeof CHANNEL_TYPES];
  expirationDate: string | null;
};

type ShareData = {
  emailAddress?: string;
  emailSubjectLine?: string;
  expirationDate?: string;
  message?: string | null;
  partMasterVersion?: string;
};

//TODO: Refine Share definition
type Share = JsonObject & {
  channelId: string;
};

export type LegacyFileTaskStatusResponse = {
  statuses: {
    interrogation: LegacyFileTaskStatusData & { uuid: string };
    processing: {
      uuid: string;
      geometricIndexing: LegacyFileTaskStatusData;
      processing: LegacyFileTaskStatusData;
      thumbnailCreation: LegacyFileTaskStatusData;
      upload: LegacyFileTaskStatusData;
      vectorization: LegacyFileTaskStatusData;
      viewerCreation: LegacyFileTaskStatusData;
    }[];
  };
};

type PartUploadResponse = {
  uuid: string;
  partMasterUuid: string;
  version: string;
  primaryFile: {
    filename: string;
    status: LegacyFileTaskStatus;
    uuid: string;
  };
};

type Interrogation = {
  id: string;
  name: string;
};

const annotations = {
  create: (channelId: string, data: AnnotationData): Promise<Annotation> =>
    prepareAndMakeRequest.post<Annotation>(
      `/part_library/channels/${channelId}/annotations`,
      { body: data },
    ),

  delete: (annotationId: string): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>(
      `/part_library/annotations/${annotationId}`,
    ),

  //Doesn't seem to be used?
  stream: (
    partUUID: string,
    { lastMessageReceived }: { lastMessageReceived: string },
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/part_library/${partUUID}/stream`, {
      query: { lastMessageReceived },
    }),
};

const channels = {
  create: (partUUID: string, data: ChannelData): Promise<Channel> =>
    prepareAndMakeRequest.post<Channel>(`/part_library/${partUUID}/channels`, {
      body: data,
    }),

  get: (channelId: string): Promise<Channel> =>
    prepareAndMakeRequest.get(`/part_library/channels/${channelId}`),

  list: (partUUID: string): Promise<Channel[]> =>
    prepareAndMakeRequest.get<Channel[]>(`/part_library/${partUUID}/channels`),

  patch: (channelId: string, data: ChannelData): Promise<JsonValue> =>
    prepareAndMakeRequest.patch<JsonValue>(
      `/part_library/channels/${channelId}`,
      { body: data },
    ),
};

const comments = {
  create: (
    annotationId: string,
    data: { content: JsonValue; parentId?: string | null },
  ): Promise<Comment> =>
    prepareAndMakeRequest.post<Comment>(
      `/part_library/annotations/${annotationId}/comments`,
      { body: data },
    ),

  delete: (commentId: string): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>(
      `/part_library/comments/${commentId}`,
    ),

  patch: (commentId: string, data: Comment): Promise<JsonValue> =>
    prepareAndMakeRequest.patch<JsonValue>(
      `/part_library/comments/${commentId}`,
      { body: data },
    ),
};

const mscChannelConfig = {
  patch: async (
    channelId: string,
    data: { mscSpecialistId: string },
  ): Promise<Channel> =>
    prepareAndMakeRequest.patch<Channel>(
      `/part_library/channels/${channelId}/msc_channel_config`,
      { body: data },
    ),
};

const sharedTechDataPackage = {
  get: (
    uuid: string,
    queryParams?: { version?: string },
  ): Promise<SharedTechnicalDataPackage> =>
    prepareAndMakeRequest.get<SharedTechnicalDataPackage>(
      `/part_library/shared_technical_data_package/${uuid}`,
      { query: queryParams },
    ),

  requestAccess: (
    uuid: string,
    data: { version: string },
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(
      `/part_library/shared_technical_data_package/${uuid}/request_access`,
      { body: data },
    ),
};

const shares = {
  create: (channelId: string, data: ShareData): Promise<Share> =>
    prepareAndMakeRequest.post<Share>(
      `/part_library/channels/${channelId}/shares`,
      { body: data },
    ),

  delete: (shareId: string): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>(`/part_library/shares/${shareId}`),

  patch: (shareId: string, data: ShareData): Promise<Share> =>
    prepareAndMakeRequest.patch<Share>(`/part_library/shares/${shareId}`, {
      body: data,
    }),
};

const suppliersMscSettings = {
  get: (): Promise<JsonObject> =>
    prepareAndMakeRequest.get<JsonObject>(`/part_library/msc/settings`),
};

const tasks = {
  patch: (
    taskId: string,
    data: { assignedToId: string; dueDate?: string; status: JsonValue },
  ): Promise<Task> =>
    prepareAndMakeRequest.patch<Task>(`/part_library/tasks/${taskId}`, {
      body: data,
    }),
};

export default {
  annotations,
  channels,
  comments,
  mscChannelConfig,
  sharedTechDataPackage,
  shares,
  suppliersMscSettings,
  tasks,

  checkStatuses: (uuids: string[]): Promise<LegacyFileTaskStatus[]> =>
    prepareAndMakeRequest.get('/part_library/statuses', { query: { uuids } }),

  get: (uuid: string): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.get<PartViewerPageData>(`/part_library/${uuid}`),

  list: async ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: PartViewerPageData[] }> =>
    prepareAndMakeRequest.get<{ results: PartViewerPageData[] }>(
      '/part_library/',
      {
        query: { ...(page && { page }), search: search ?? '' },
      },
    ),

  listSharedParts: ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: Share }> =>
    prepareAndMakeRequest.get<{ results: Share }>(
      '/part_library/shared_with_me/',
      { query: { ...(page && { page }), search: search ?? '' } },
    ),

  listArchivedParts: ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: PartViewerPageData; count: number }> =>
    prepareAndMakeRequest.get<{ results: PartViewerPageData; count: number }>(
      '/part_library/archived_parts/',
      { query: { ...(page && { page }), search: search ?? '' } },
    ),

  partUpload: (formData: FormData): Promise<PartUploadResponse> =>
    prepareAndMakeRequest.post<PartUploadResponse>(
      '/part_library/file_upload',
      { body: formData },
    ),

  getTechnicalDataPackage: (
    uuid: string,
    queryParams?: { version?: string; quoteItemId?: string },
  ): Promise<TechnicalDataPackage> =>
    prepareAndMakeRequest.get<TechnicalDataPackage>(
      `/part_library/technical_data_package/${uuid}`,
      { query: queryParams },
    ),

  supportingFileUpload: ({
    formData,
    uuid,
  }: {
    formData: FormData;
    uuid: string;
  }): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.post<PartViewerPageData>(
      `/part_library/${uuid}/files`,
      {
        body: formData,
      },
    ),

  listCustomInterrogations: ({
    uuid,
    params,
  }: {
    uuid: string;
    params: { task: string };
  }): Promise<Interrogation[]> =>
    prepareAndMakeRequest.get<Interrogation[]>(
      `/part_library/${uuid}/custom_interrogations`,
      { query: params },
    ),

  interrogationRequest: ({
    uuid,
    data,
  }: {
    uuid: string;
    data: { customInterrogationId: string; materialId: string; task: string };
  }): Promise<Record<string, LegacyFileTaskStatusData>> =>
    prepareAndMakeRequest.post<Record<string, LegacyFileTaskStatusData>>(
      `/part_library/${uuid}/interrogation_request`,
      { body: data },
    ),
};
